import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { buildGlossaryRegex, lookupTerm } from "@/lib/glossary";

/**
 * GlossaryEnhancer
 *
 * A site-wide, zero-config enhancement that automatically finds every
 * abbreviation we explain in the glossary (SEO, AEO, GEO, PPC, …) inside the
 * visible page content and turns each one into an interactive term. Clients
 * can hover (desktop) or tap (mobile/keyboard) to reveal a small bubble with
 * a plain-English explanation.
 *
 * It works by scanning text nodes after each navigation (and when content
 * changes) and wrapping matches in a styled <span class="glossary-term">.
 * A single shared bubble element is positioned next to whichever term is
 * active. Re-scanning is idempotent — already-wrapped terms are skipped.
 */

// Containers / elements we should never rewrite inside. We intentionally
// allow links, buttons and labels so acronyms stay interactive everywhere
// (their tap is stopped from triggering the parent link/accordion below).
const SKIP_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "CODE",
  "PRE",
  "KBD",
  "SAMP",
  "TEXTAREA",
  "INPUT",
  "SELECT",
  "OPTION",
  "SVG",
  "CANVAS",
  "VIDEO",
  "AUDIO",
]);

let bubbleEl: HTMLDivElement | null = null;
let activeTerm: HTMLElement | null = null;
let listenersAttached = false;
let lastTouchAt = 0;
// Tracks whether we've completed the initial (post-hydration) scan. On the very
// first load we MUST NOT mutate the server-rendered DOM until React has finished
// hydrating it — otherwise splitting a text node (e.g. "GDPR" in a testimonial)
// mid-hydration makes React detect a mismatch and regenerate the whole tree,
// which shows up as a page-wide flicker/flash. Subsequent client navigations
// have no SSR markup to reconcile, so they can scan immediately.
let hasHydratedOnce = false;

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function ensureBubble(): HTMLDivElement {
  if (bubbleEl) return bubbleEl;
  const el = document.createElement("div");
  el.className = "glossary-bubble";
  el.setAttribute("role", "tooltip");
  el.setAttribute("data-glossary-skip", "");
  el.style.display = "none";
  document.body.appendChild(el);
  bubbleEl = el;
  return el;
}

function hideBubble() {
  if (bubbleEl) bubbleEl.style.display = "none";
  if (activeTerm) {
    activeTerm.removeAttribute("data-glossary-open");
    activeTerm = null;
  }
}

function showBubble(target: HTMLElement) {
  const term = target.dataset.term;
  if (!term) return;
  const entry = lookupTerm(term);
  if (!entry) return;

  const el = ensureBubble();
  el.innerHTML = "";

  const title = document.createElement("div");
  title.className = "glossary-bubble-title";
  title.textContent = `${term} — ${entry.full}`;

  const body = document.createElement("div");
  body.className = "glossary-bubble-body";
  body.textContent = entry.text;

  el.appendChild(title);
  el.appendChild(body);
  el.style.display = "block";

  // Measure then position (fixed = viewport coordinates).
  const rect = target.getBoundingClientRect();
  const bw = el.offsetWidth;
  const bh = el.offsetHeight;
  const margin = 8;

  let left = rect.left + rect.width / 2 - bw / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - bw - margin));

  let top = rect.top - bh - 10;
  let placement = "top";
  if (top < margin) {
    top = rect.bottom + 10;
    placement = "bottom";
  }

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
  el.dataset.placement = placement;

  if (activeTerm && activeTerm !== target) {
    activeTerm.removeAttribute("data-glossary-open");
  }
  activeTerm = target;
  target.setAttribute("data-glossary-open", "");
}

function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  document.addEventListener("touchstart", () => {
    lastTouchAt = Date.now();
  }, { passive: true });

  // Desktop hover.
  document.addEventListener("mouseover", (e) => {
    if (Date.now() - lastTouchAt < 700) return; // ignore synthetic hover after tap
    const target = (e.target as HTMLElement | null)?.closest?.(".glossary-term") as HTMLElement | null;
    if (target) showBubble(target);
  });
  document.addEventListener("mouseout", (e) => {
    if (Date.now() - lastTouchAt < 700) return;
    const target = (e.target as HTMLElement | null)?.closest?.(".glossary-term") as HTMLElement | null;
    if (target && target === activeTerm) hideBubble();
  });

  // Tap / click toggles (mobile + desktop). Capture phase so we run before
  // any parent link/accordion handler and can stop the term's tap from also
  // navigating or toggling the element it sits inside.
  document.addEventListener(
    "click",
    (e) => {
      const target = (e.target as HTMLElement | null)?.closest?.(".glossary-term") as HTMLElement | null;
      if (target) {
        // If the term sits inside an interactive element (a link, button, or
        // anything with a button/link role), the user almost certainly wants
        // to activate that control — e.g. a "#AEO" tag filter button. In that
        // case we must NOT swallow the click: let it bubble to the parent so
        // the link/button still fires. The plain-English meaning still shows
        // on hover (desktop) via the mouseover handler above.
        const interactiveParent = target.parentElement?.closest?.(
          'a, button, [role="button"], [role="link"], summary, label, [onclick]',
        );
        if (interactiveParent) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (activeTerm === target && bubbleEl?.style.display === "block") {
          hideBubble();
        } else {
          showBubble(target);
        }
        return;
      }
      if (!(e.target as HTMLElement | null)?.closest?.(".glossary-bubble")) {
        hideBubble();
      }
    },
    true,
  );

  // Keyboard accessibility.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      hideBubble();
      return;
    }
    const target = (e.target as HTMLElement | null)?.closest?.(".glossary-term") as HTMLElement | null;
    if (target && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      if (activeTerm === target && bubbleEl?.style.display === "block") hideBubble();
      else showBubble(target);
    }
  });

  // Keep the bubble glued to its term (or hide it) as the page moves.
  window.addEventListener("scroll", hideBubble, { passive: true });
  window.addEventListener("resize", hideBubble);
}

function wrapTextNode(node: Text, regex: RegExp) {
  const text = node.nodeValue ?? "";
  regex.lastIndex = 0;
  const frag = document.createDocumentFragment();
  let last = 0;
  let match: RegExpExecArray | null;
  let found = false;

  while ((match = regex.exec(text)) !== null) {
    found = true;
    const term = match[0];
    const start = match.index;
    if (start > last) {
      frag.appendChild(document.createTextNode(text.slice(last, start)));
    }
    const span = document.createElement("span");
    span.className = "glossary-term";
    span.dataset.term = term;
    span.textContent = term;
    span.setAttribute("tabindex", "0");
    span.setAttribute("role", "button");
    span.setAttribute("aria-label", `${term}: tap or hover for a plain-English explanation`);
    frag.appendChild(span);
    last = start + term.length;
  }

  if (!found) return;
  if (last < text.length) {
    frag.appendChild(document.createTextNode(text.slice(last)));
  }

  // If the text node lives directly inside a flex/grid container, replacing it
  // with loose text + spans would turn every fragment into a separate flex/grid
  // item — so the container's `gap` injects spurious spacing between the words
  // around each term (the card spacing bug). Wrapping everything in one inline
  // span keeps it as a single layout item while the terms flow as normal text.
  const parent = node.parentElement;
  let useWrapper = false;
  if (parent && typeof getComputedStyle === "function") {
    const display = getComputedStyle(parent).display;
    if (display === "flex" || display === "inline-flex" || display === "grid" || display === "inline-grid") {
      useWrapper = true;
    }
  }

  if (useWrapper) {
    const wrapper = document.createElement("span");
    wrapper.setAttribute("data-glossary-wrapper", "");
    wrapper.appendChild(frag);
    node.parentNode?.replaceChild(wrapper, node);
  } else {
    node.parentNode?.replaceChild(frag, node);
  }
}


function enhance(regex: RegExp) {
  const root = document.querySelector("main") || document.body;
  if (!root) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const text = node.nodeValue;
      if (!text || text.length < 2) return NodeFilter.FILTER_REJECT;
      let el: HTMLElement | null = node.parentElement;
      while (el) {
        if (SKIP_TAGS.has(el.tagName)) return NodeFilter.FILTER_REJECT;
        if (el.classList?.contains("glossary-term")) return NodeFilter.FILTER_REJECT;
        if (el.hasAttribute?.("data-glossary-skip")) return NodeFilter.FILTER_REJECT;
        if (el.isContentEditable) return NodeFilter.FILTER_REJECT;
        el = el.parentElement;
      }
      regex.lastIndex = 0;
      return regex.test(text) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const targets: Text[] = [];
  while (walker.nextNode()) targets.push(walker.currentNode as Text);
  for (const node of targets) wrapTextNode(node, regex);
}

export function GlossaryEnhancer() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (!isBrowser()) return;
    attachListeners();

    const regex = buildGlossaryRegex();
    let raf = 0;
    let settle: ReturnType<typeof setTimeout> | undefined;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    let observer: MutationObserver | null = null;
    let startTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => enhance(regex));
    };

    // Begin scanning + watching for content changes. The first pass mutates the
    // DOM, so on the initial load it is held until hydration is complete (see
    // `hasHydratedOnce`); after that every run is safe.
    const begin = () => {
      if (cancelled) return;
      run();
      settle = setTimeout(run, 350);
      observer = new MutationObserver((mutations) => {
        // Ignore mutations that originate inside a glossary-skip region (e.g. the
        // live messaging UI). Those surfaces re-render constantly via realtime
        // updates and the presence ping; re-walking and rewriting their text
        // nodes tears the chat header/messages out and back in — the white
        // flash / ghost-text flicker. Skipping them here also avoids a needless
        // full-page rescan on every incoming message.
        const relevant = mutations.some((m) => {
          const node = (m.target.nodeType === 1 ? m.target : m.target.parentElement) as HTMLElement | null;
          return node ? !node.closest('[data-glossary-skip]') : true;
        });
        if (!relevant) return;
        if (debounce) clearTimeout(debounce);
        debounce = setTimeout(run, 400);
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    };

    if (hasHydratedOnce) {
      // Client-side navigation: no SSR markup to reconcile, scan right away.
      begin();
    } else {
      // First load: wait until React has fully hydrated the server markup before
      // touching the DOM, so we never trigger a hydration mismatch + flicker.
      const onReady = () => {
        if (cancelled) return;
        hasHydratedOnce = true;
        // One more frame to let any lazy/suspended subtree finish hydrating.
        startTimer = setTimeout(begin, 0);
      };
      if (document.readyState === 'complete') {
        startTimer = setTimeout(onReady, 200);
      } else {
        window.addEventListener('load', () => {
          startTimer = setTimeout(onReady, 200);
        }, { once: true });
      }
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (settle) clearTimeout(settle);
      if (startTimer) clearTimeout(startTimer);
      if (debounce) clearTimeout(debounce);
      cancelAnimationFrame(raf);
      hideBubble();
    };
  }, [pathname]);

  return null;
}

export default GlossaryEnhancer;
